import { Diamond, EditOutlined, DeleteOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Switch,
  Tooltip,
  Grid,
  Typography,
  Chip,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Paper,
  Container,
  Fade,
} from "@mui/material";
import  { useEffect, useState } from "react";
import TableDropDown from "../../components/shared/TableDropDown"; 
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import errorHandler from "../../utils/errorHandler";
import { capitalize } from "../../utils/helpers";
import { formatCash } from "../../utils/formatNumber";
import { toast } from "react-toastify";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { gemPackagesServices } from "../../services/AllServices";
import { Loader } from "../../components/CustomLoader";

export default function GemPackagesManagementList() {
  const navigate = useNavigate();
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [data, setData] = useState();
  const [warning, setWarning] = useState("");
  const [action, setAction] = useState("");
  const [show, setShow] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [status, setStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [viewMode, setViewMode] = useState("grid");

  const statusMap = {
    0 : "Inactive", 1 : "Active",
  };

  const columns = [
    {
      name: "S.No.",
      sortable: false,
      cell: (row, index) => (index+1) ,
      width: "100px",
    },
    {
      name: "Package Name",
      sortable: true,
      selector: (row) => row.packageName || "-",
    },
    {
      name: "Gem Amount",
      sortable: true,
      selector: (row) => row.gemAmount,
    },
    {
      name: "Price",
      sortable: true,
      selector: (row) => formatCash(row.price),
    },
    {
      name: "Created At",
      sortable: true,
      selector: (row) => capitalize(new Date(row.createdAt).toLocaleDateString()), // Basic fallback if fDate is missing
    },
    {
      name: "Status",
      cell: (row) => (
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={row.status === 1}
                onClick={() => {
                  setShow(true);
                  setCurrentStatus(row.status);
                  setStatusId(row._id);
                  setAction("status");
                  setWarning(
                    `Are you sure you want to ${
                      row.status === 1 ? "deactivate" : "activate"
                    } this package?`
                  );
                }}
                inputProps={{ "aria-label": "controlled" }}
              />
            }
          />
        </FormGroup>
      ),
    },
    {
      name: "Action",
      center: true,
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Edit" placement="left">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/gem-packages/edit/${row?._id}`);
              }}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" placement="top">
            <IconButton
           
              color="error"
              onClick={() => {
                setShow(true);
                setStatusId(row._id);
                setAction("delete");
                setWarning("Are you sure you want to delete this gem package?");
              }}
            >
              <DeleteOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDebouncedSearch();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, []);

  useEffect(() => {
    const body = {
      status: status 
    };
    setIsLoading(true);
    setLoaderDelay(false);
    gemPackagesServices
      .getAllGemPackages(body)
      .then((res) => {
        if (res.status === 200) {
          setTimeout(() => {
            setData(res?.data?.data?.result);
            setLoaderDelay(true);
          }, 500);
        }
      })
      .catch((err) => {
        setIsLoading(false);
        errorHandler(err);
        setLoaderDelay(true);
      })
      .finally(() => {
        setIsLoading(false);
        setLoaderDelay(false);
      });
  }, [debouncedSearch, refresh, status]);

  const toggleChecked = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;

      const currentPackage = data.find((pkg) => pkg._id === id);

      const payload = {
        id: id,
        gemAmount: currentPackage.gemAmount,
        price: currentPackage.price,
        status: newStatus,
      };

      const res = await gemPackagesServices.updateGemPackages(payload);

      setCurrentStatus("");

      if (res && (res.status === 200 || res.status === 201)) {
        const message = res?.data?.message || "Status updated successfully";
        toast.success(message, { toastId: "success" });

        setData((prevData) =>
          prevData.map((pkg) =>
            pkg._id === id ? { ...pkg, status: newStatus } : pkg
          )
        );

        setShow(false);
      } else {
        const errorMessage = res?.data?.message || "Failed to update status";
        toast.error(errorMessage, { toastId: "error1" });
        setShow(false);
      }
    } catch (err) {
      setShow(false);
      errorHandler(err);
    }
  };

  const confirmDelete = async () => {
    try {
      const res = await gemPackagesServices.deleteGemPackages({ id: statusId });

      if (res && (res.status === 200 || res.status === 201)) {
        const message = res?.data?.message || "Package deleted successfully";
        toast.success(message, { toastId: "del-success" });
        
        setRefresh(!refresh);
        setShow(false);
      } else {
        const errorMessage = res?.data?.message || "Failed to delete package";
        toast.error(errorMessage, { toastId: "del-error" });
        setShow(false);
      }
    } catch (err) {
      setShow(false);
      errorHandler(err);
    }
  };

  const renderPackageCard = (pkg, index) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={pkg._id}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          background:
            "linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.05) 100%)",
          border: "1px solid",
          borderColor: "divider",
          transition: "all 0.3s ease",
          "&:hover": {
            borderColor: "primary.main",
            transform: "translateY(-4px)",
            boxShadow: "0 12px 40px rgba(0, 161, 221, 0.2)",
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Avatar
            sx={{
              width: 48,
              height: 48,
              background: "linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)",
              boxShadow: "0 4px 16px rgba(0, 161, 221, 0.3)",
            }}
          >
            <Diamond sx={{ fontSize: 24, color: "white" }} />
          </Avatar>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={pkg.status === 1}
                    onClick={() => {
                      setShow(true);
                      setCurrentStatus(pkg.status);
                      setStatusId(pkg._id);
                      setAction("status");
                      setWarning(
                        `Are you sure you want to ${
                          pkg.status === 1 ? "deactivate" : "activate"
                        } this package?`
                      );
                    }}
                    inputProps={{ "aria-label": "controlled" }}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#4caf50",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#4caf50",
                        },
                    }}
                  />
                }
                label=""
              />
            </FormGroup>
            <Chip
              label={pkg.status === 1 ? "Active" : "Inactive"}
              size="small"
              sx={{
                backgroundColor:
                  pkg.status === 1
                    ? "rgba(76, 175, 80, 0.2)"
                    : "rgba(244, 67, 54, 0.2)",
                color: pkg.status === 1 ? "#4caf50" : "#f44336",
                fontWeight: 600,
                border: "1px solid",
                borderColor:
                  pkg.status === 1
                    ? "rgba(76, 175, 80, 0.3)"
                    : "rgba(244, 67, 54, 0.3)",
              }}
            />
          </Stack>
        </Stack>

        <Box sx={{ mb: 3 }}>
          {pkg.packageName && (
            <Typography
              variant="h6"
              sx={{ color: "white", fontWeight: 700, mb: 0.5 }}
            >
              {pkg.packageName}
            </Typography>
          )}
          <Typography
            variant={pkg.packageName ? "subtitle1" : "h6"}
            sx={{ color: pkg.packageName ? "rgba(255, 255, 255, 0.8)" : "white", fontWeight: pkg.packageName ? 600 : 700, mb: 1 }}
          >
            {pkg.gemAmount} Gems
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Price: {formatCash(pkg.price)}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.disabled" }}>
            Created: {new Date(pkg.createdAt).toLocaleDateString()}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            startIcon={<EditOutlined />}
            onClick={() => navigate(`/gem-packages/edit/${pkg._id}`)}
            sx={{
              borderRadius: 2,
              background: "linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #0091c8 0%, #3b8fd9 100%)",
              },
            }}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlined />}
            onClick={() => {
              setShow(true);
              setStatusId(pkg._id);
              setAction("delete");
              setWarning("Are you sure you want to delete this gem package?");
            }}
            sx={{
              borderRadius: 2,
              "&:hover": {
                backgroundColor: "rgba(255, 0, 0, 0.32)",
                transition: "0.2s"
              }
            }}
          >
            Delete
          </Button>
        </Stack>
      </Paper>
    </Grid>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Stack spacing={4}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              background: "linear-gradient(135deg, #1a202c 0%, #2d3748 100%)",
              border: "1px solid",
              borderColor: "divider",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background:
                  "linear-gradient(90deg, #00a1dd 0%, #42a5f5 50%, #90caf9 100%)",
              },
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
             
            >
              <Stack direction="row" alignItems="center" spacing={3}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    background:
                      "linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)",
                    boxShadow: "0 8px 32px rgba(0, 161, 221, 0.4)",
                  }}
                >
                  <Diamond sx={{ fontSize: 32, color: "white" }} />
                </Avatar>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{ color: "white", fontWeight: 700, mb: 1 }}
                  >
                    Gem Packages Management
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                  >
                    Manage and monitor all gem packages in your system
                  </Typography>
                </Box>
              </Stack>
              <Button
                size="large"
                variant="contained"
                onClick={() => navigate("/gem-packages/add")}
                sx={{
                  borderRadius: 2,
                  background:
                    "linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)",
                  boxShadow: "0 8px 32px rgba(0, 161, 221, 0.3)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #0091c8 0%, #3b8fd9 100%)",
                    boxShadow: "0 12px 40px rgba(0, 161, 221, 0.4)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                + Add Package
              </Button>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              background: "rgba(255, 255, 255, 0.02)",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ flexWrap: "wrap", gap: 2 }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ flexWrap: "wrap", gap: 2 }}
              >
               <TableDropDown
                  filterName="Status"
                  data={Object.entries(statusMap).map(([key, value]) => ({
                    value: parseInt(key),
                    label: capitalize(value),
                  }))}
                  value={status} 
                  setValue={setStatus} 
                />
              </Stack>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => newMode && setViewMode(newMode)}
                sx={{
                  "& .MuiToggleButton-root": {
                    color: "text.secondary",
                    borderColor: "divider",
                    "&.Mui-selected": {
                      backgroundColor: "primary.main",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "primary.dark",
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="grid">
                  <ViewModuleIcon />
                </ToggleButton>
                <ToggleButton value="list">
                  <ViewListIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              background: "rgba(255, 255, 255, 0.02)",
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            {viewMode === "grid" ? (
              <Box sx={{ p: 3 }}>
                {isLoading || !loaderDelay ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 8 }}
                  >
                    <Loader />
                  </Box>
                ) : !data || data.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 8 }}>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        mx: "auto",
                        mb: 3,
                        background:
                          "linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)",
                        boxShadow: "0 8px 32px rgba(156, 39, 176, 0.3)",
                      }}
                    >
                      <Diamond sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      No gem packages found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your search criteria or create a new package
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Grid container spacing={3}>
                      {data.map((pkg, index) =>
                        renderPackageCard(pkg, index)
                      )}
                    </Grid>
                  </>
                )}
              </Box>
            ) : (
              <Box>
                <DataTable
                  customStyles={{
                    headCells: {
                      style: {
                        fontSize: "15px",
                        fontWeight: "bold",
                        backgroundColor: "rgba(255, 255, 255, 0.02)",
                        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                      },
                    },
                    rows: {
                      style: {
                        backgroundColor: "transparent",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.02)",
                        },
                      },
                    },
                  }}
                  columns={columns}
                  data={data}
                  progressPending={isLoading || !loaderDelay}
                  progressComponent={<Loader />}
                  className="custom"
                />
              </Box>
            )}
          </Paper>
        </Stack>
      </Fade>

      <Dialog
        open={show}
        onClose={() => setShow(false)}
        className="popupbox-statis"
      >
        <DialogTitle>{warning}</DialogTitle>
        <DialogActions className="status-box-action">
          <Button
            variant="contained"
            onClick={() => {
              if (action === "status") {
                toggleChecked(statusId, currentStatus);
              } else if (action === "delete") {
                confirmDelete();
              }
            }}
            color={action === "delete" ? "error" : "primary"}
            sx={{ borderRadius: 2 }}
          >
            Yes
          </Button>
          <Button
            variant="outlined"
            onClick={() => setShow(false)}
            color="primary"
            sx={{ borderRadius: 2 }}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}